/**
 * These are the individual rows in a filter panel.
 */
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import StopOutlined from '@ant-design/icons/StopOutlined'
import { Button, Select, Tooltip } from 'antd'
import { useGetPrecedingFilters } from 'features/drilldown'
import { DrilldownTypeAndFieldSelect } from 'features/drilldown/components/fieldSelect/TypeAndFieldSelect'
import {
  EnrichedOntologyFieldDefinition,
  ONTOLOGY_FIELD_INPUTS,
  OntologyField,
  OntologyFieldType,
  OntologyValueType,
  useOntology,
} from 'features/dynamicOntology'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { RolodexHistogramFilter, RolodexHistogramFilterOp } from 'types'

import { Filter } from './DrilldownFilterView'

export type DrilldownFilterRowProps = {
  filter: Filter
  onRemoveFilter: () => void
  setFilterState: Dispatch<SetStateAction<Filter['state']>>
  isActive: boolean
  allowFilterOps: ReadonlyArray<RolodexHistogramFilterOp> | undefined
}

const FilterRowContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`

const FilterWarningIcon = styled(StopOutlined)`
  font-size: 16px;
  color: ${CantileverTheme.colors.DeepRed};
  margin-left: 8px;
`

const FilterFieldSelect = styled(DrilldownTypeAndFieldSelect)`
  min-width: 200px;
`

const FilterOperatorSelect = styled(Select)`
  min-width: 130px;
`

const FilterValueContainer = styled.div<{ $isValueEditable: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  min-width: ${({ $isValueEditable }) => ($isValueEditable ? '400px' : '50px')};
  max-width: 700px;
  gap: 0.5rem;
`

const RemoveFilterButton = styled(Button)`
  color: ${CantileverTheme.colors.gray['600']};
  transition: all 0.2s ease;

  &:hover {
    color: ${CantileverTheme.colors.DeepRed};
  }
`

/**
 * Display names to show in the 'operator' select
 */
export const OPERATOR_NAMES: Record<RolodexHistogramFilterOp, string> = {
  '=': 'Equals',
  '!=': 'Not Equal to',
  '~': 'Contains',
  '>': 'Greater than',
  '<': 'Less than',
  '>=': 'Greater than or equal to',
  '<=': 'Less than or equal to',
  in: 'In',
  notin: 'Not in',
  null: 'Is Null',
  notnull: 'Is not Null',
  contains: 'Contains (Array)', // Postgres: @>
  truthy: 'True',
  falsy: 'False',
}

/**
 * Which operators to show for each field type
 */
export const FIELD_TYPE_OPERATORS: Record<
  OntologyFieldType,
  readonly RolodexHistogramFilter['op'][]
> = {
  text: ['~', '=', '!='],
  number: ['=', '!=', '>', '<', '<=', '>='],
  enum: ['in', 'notin'],
  boolean: ['truthy', 'falsy'],
  date: ['=', '!=', '<', '>', '<=', '>='],
  // TODO: Add support for object and array filters in the future
  object: [],
  array: [],
}

/**
 * Operators to show for 'unary' fields, which don't require a value input.
 */
export const UNARY_OPERATORS: readonly RolodexHistogramFilterOp[] = [
  'null',
  'notnull',
  'truthy',
  'falsy',
]

/**
 * Operators that are allowed for all property types.
 */
export const GLOBAL_OPERATORS: readonly RolodexHistogramFilterOp[] = [
  'null',
  'notnull',
]

export interface FilterState {
  field: OntologyField
  fieldDef: EnrichedOntologyFieldDefinition
  operator: RolodexHistogramFilterOp
  value?: OntologyValueType
}

/**
 * This component handles the selection for a field and an operator,
 * and then renders the appropriate value-entry component based on the
 * type of the field.
 * @returns the filter row
 */
const RawDrilldownFilterRow: FC<DrilldownFilterRowProps> = ({
  filter,
  onRemoveFilter,
  setFilterState,
  isActive,
  allowFilterOps,
}) => {
  const { state: filterState } = filter
  const { operator, field, fieldDef, value } = filterState

  const { fields } = useOntology()
  const precedingFilters = useGetPrecedingFilters()

  const fieldType = fieldDef?.type ?? 'text'

  const possibleOperators = useMemo(
    () => [...(FIELD_TYPE_OPERATORS[fieldType] ?? []), ...GLOBAL_OPERATORS],
    [fieldType]
  )
  // If operator is invalid, reset to the first valid operator for the field
  useEffect(() => {
    if (!possibleOperators.includes(operator) && possibleOperators.length) {
      setFilterState((prev) => ({
        ...prev,
        operator: possibleOperators[0],
        value: undefined,
      }))
    }
  }, [operator, possibleOperators, setFilterState])

  const operatorOptions = useMemo(
    () =>
      possibleOperators
        .filter((op) => !allowFilterOps || allowFilterOps.includes(op))
        .map((operator) => ({
          value: operator,
          label: OPERATOR_NAMES[operator],
        })),
    [allowFilterOps, possibleOperators]
  )

  const isValueEditable = useMemo(() => {
    return !UNARY_OPERATORS.includes(operator)
  }, [operator])

  const FieldInputComponent = useMemo(() => {
    if (!fieldDef) {
      return undefined
    }
    return fieldDef.type === 'boolean'
      ? () => null
      : ONTOLOGY_FIELD_INPUTS[fieldType]
  }, [fieldDef, fieldType])

  const handleValueChange = useCallback(
    (newValue?: OntologyValueType) => {
      setFilterState((prev) => ({
        ...prev,
        value: newValue,
      }))
    },
    [setFilterState]
  )

  const setSelectedField = useCallback(
    (val: SetStateAction<OntologyField>) => {
      setFilterState((prev) => {
        const oldVal = prev.field
        const selectedField = typeof val === 'function' ? val(oldVal) : val
        const nextFieldDef = fields[selectedField]
        if (!nextFieldDef) {
          return prev
        }
        const selectedFieldType = nextFieldDef.type ?? 'text'
        const validOperators = FIELD_TYPE_OPERATORS[selectedFieldType] ?? []
        const defaultOperator =
          validOperators[0] ?? GLOBAL_OPERATORS[0] ?? prev.operator
        const shouldKeepOperator = validOperators.includes(prev.operator)
        return {
          field: selectedField,
          fieldDef: nextFieldDef,
          operator: shouldKeepOperator ? prev.operator : defaultOperator,
          value: shouldKeepOperator ? prev.value : undefined,
        }
      })
    },
    [fields, setFilterState]
  )

  return (
    <FilterRowContainer>
      <FilterFieldSelect
        selectedField={field}
        setSelectedField={setSelectedField}
      />
      <FilterOperatorSelect
        value={operator}
        options={operatorOptions}
        onChange={(value) => {
          setFilterState((prev) => ({
            ...prev,
            operator: value as RolodexHistogramFilterOp,
            value: undefined,
          }))
        }}
        popupMatchSelectWidth={false}
      />
      <FilterValueContainer $isValueEditable={isValueEditable}>
        {isValueEditable && FieldInputComponent && fieldDef ? (
          <FieldInputComponent
            field={field}
            fieldDef={fieldDef}
            value={value}
            onChange={handleValueChange}
            filters={precedingFilters}
            allowMultiple={true}
          />
        ) : null}
        {!isActive && (
          <Tooltip title='Filter not applied - please supply a value'>
            <FilterWarningIcon />
          </Tooltip>
        )}
        <RemoveFilterButton type='text' onClick={onRemoveFilter}>
          <CloseOutlined />
        </RemoveFilterButton>
      </FilterValueContainer>
    </FilterRowContainer>
  )
}

/**
 * Memo'd filter row (keeps things feeling snappy when you have a lot of them).
 */
export const DrilldownFilterRow = React.memo(RawDrilldownFilterRow)
